/**
 * Feature: Límite máximo de compra — CAPA 2 (interceptor global de JavaScript).
 *
 * Envuelve `window.fetch` para inspeccionar CUALQUIER POST a `/cart/add` que pase por fetch
 * (formulario de producto, quick-add, "agregar" de tarjetas, o llamadas programáticas del tema),
 * consulta el estado real del carrito vía `GET /cart.js` y bloquea el POST si excede el límite.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ ALCANCE Y LÍMITES (leer):
 *   - Esto es defensa en profundidad a nivel de UX/cliente, NO seguridad real.
 *   - Un atacante puede llamar a `/cart/add` con curl/Postman, o capturar `window.fetch` antes
 *     de que este script lo envuelva, o usar XHR. NO podemos impedir eso desde el navegador.
 *   - La verificación verdaderamente server-side en plan estándar es el webhook `orders/create`
 *     (Capa 4). Ver docs/purchase-limit-webhook.md.
 *   - Por seguridad de la conversión: ante CUALQUIER duda (no podemos determinar el límite,
 *     error de red, body ilegible) este guard DEJA PASAR la petición. Solo bloquea cuando puede
 *     determinar positivamente una violación.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 */

(() => {
  const originalFetch = window.fetch.bind(window);

  /** Rutas que cuentan como "agregar al carrito". */
  const isCartAddUrl = (url) => {
    if (typeof url !== 'string') return false;
    // Cubre /cart/add y /cart/add.js, con o sin locale prefix (/es/cart/add...).
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  };

  /** Extrae {variantId, quantity} del body de la petición, sea FormData, URLSearchParams o JSON. */
  const parseAddPayload = async (init) => {
    try {
      const body = init?.body;
      if (!body) return null;

      if (body instanceof FormData) {
        return {
          variantId: String(body.get('id') ?? ''),
          quantity: Number(body.get('quantity') ?? 1) || 1,
        };
      }
      if (body instanceof URLSearchParams) {
        return {
          variantId: String(body.get('id') ?? ''),
          quantity: Number(body.get('quantity') ?? 1) || 1,
        };
      }
      if (typeof body === 'string') {
        // Puede ser JSON o urlencoded.
        try {
          const json = JSON.parse(body);
          return {
            variantId: String(json.id ?? ''),
            quantity: Number(json.quantity ?? 1) || 1,
          };
        } catch {
          const params = new URLSearchParams(body);
          return {
            variantId: String(params.get('id') ?? ''),
            quantity: Number(params.get('quantity') ?? 1) || 1,
          };
        }
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  /**
   * Busca, en el DOM, el límite efectivo y el product_id para un variant_id dado.
   * Se apoya en los data-pl-* embebidos por purchase-limit.liquid en <product-form-component>.
   * @returns {{effectiveLimit:number|null, productId:string|null, state:string, dataset:DOMStringMap}|null}
   */
  const findLimitForVariant = (variantId) => {
    const forms = document.querySelectorAll('product-form-component[data-pl-effective-limit]');
    for (const form of forms) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && String(variantInput.value) === String(variantId)) {
        const ds = /** @type {HTMLElement} */ (form).dataset;
        const maxCart = parseInt(ds.plMaxCart || '0', 10) || 0;
        const maxCustomer = parseInt(ds.plMaxCustomer || '0', 10) || 0;
        // Sin límites configurados → no aplica.
        if (maxCart === 0 && maxCustomer === 0) return null;
        const eff = parseInt(ds.plEffectiveLimit || '', 10);
        return {
          effectiveLimit: Number.isNaN(eff) ? null : eff,
          productId: ds.productId || null,
          state: ds.plState || 'ok',
          dataset: ds,
          form,
        };
      }
    }
    return null;
  };

  /** Suma la cantidad ya presente en el carrito para todas las variantes de un product_id. */
  const getCartQuantityForProduct = async (productId, variantId) => {
    try {
      const res = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (productId) {
        return items
          .filter((it) => String(it.product_id) === String(productId))
          .reduce((sum, it) => sum + (it.quantity || 0), 0);
      }
      // Sin product_id conocido → fallback a la variante.
      return items
        .filter((it) => String(it.variant_id) === String(variantId))
        .reduce((sum, it) => sum + (it.quantity || 0), 0);
    } catch {
      return null;
    }
  };

  /** Construye una respuesta 422 equivalente a la de Shopify para que los callers la manejen igual. */
  const buildBlockedResponse = (message) => {
    const payload = {
      status: 422,
      message,
      description: message,
      // Bandera propia para depuración / analítica.
      purchase_limit_blocked: true,
    };
    return new Response(JSON.stringify(payload), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  /** Muestra el mensaje de forma VISIBLE en el contenedor de error del product-form, si existe. */
  const displayInForm = (form, message) => {
    if (!form || !message) return;
    const errorEl = form.querySelector('[ref="addToCartTextError"]');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.classList.remove('hidden');
    const textNode = Array.from(errorEl.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorEl.appendChild(document.createTextNode(message));
    }
    setTimeout(() => errorEl.classList.add('hidden'), 6000);
  };

  /** Notifica a la UI: muestra el mensaje visible en el form (si hay) y deja rastro en consola. */
  const notifyBlocked = (message, form) => {
    displayInForm(form, message);
    try {
      document.dispatchEvent(
        new CustomEvent('cart:error', { bubbles: true, detail: { message, source: 'purchase-limit-guard' } })
      );
    } catch {
      /* ignore */
    }
    console.warn('[purchase-limit-guard] Add to cart bloqueado:', message);
  };

  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : input?.url;

    // Solo nos interesa el POST a /cart/add.
    const method = (init?.method || (typeof input !== 'string' && input?.method) || 'GET').toUpperCase();
    if (method !== 'POST' || !isCartAddUrl(url)) {
      return originalFetch(input, init);
    }

    const payload = await parseAddPayload(init);
    // Si no podemos leer el body → dejar pasar (no rompemos nada).
    if (!payload || !payload.variantId) {
      return originalFetch(input, init);
    }

    const limit = findLimitForVariant(payload.variantId);
    // No hay límite conocido en el DOM para esta variante → dejar pasar.
    if (!limit) {
      return originalFetch(input, init);
    }

    // Cliente debe iniciar sesión, o ya alcanzó su tope histórico → bloquear de inmediato.
    if (limit.state === 'login') {
      const msg = (limit.dataset.plMsgLogin || 'Inicia sesión para continuar.').trim();
      notifyBlocked(msg, limit.form);
      return buildBlockedResponse(msg);
    }
    if (limit.state === 'blocked' || (limit.effectiveLimit !== null && limit.effectiveLimit <= 0)) {
      const msg = (limit.dataset.plMsgCustomerReached || '').trim();
      notifyBlocked(msg, limit.form);
      return buildBlockedResponse(msg);
    }

    // Verificación contra el carrito real.
    if (limit.effectiveLimit !== null) {
      const currentQty = await getCartQuantityForProduct(limit.productId, payload.variantId);
      // Si no pudimos leer el carrito → dejar pasar (no bloqueamos por un error de red).
      if (currentQty !== null && currentQty + payload.quantity > limit.effectiveLimit) {
        const remaining = Math.max(limit.effectiveLimit - currentQty, 0);
        const msg =
          remaining <= 0
            ? (limit.dataset.plMsgCartReached || '').replace(/\{\{\s*max\s*\}\}/g, String(limit.effectiveLimit))
            : (limit.dataset.plMsgCart || '')
                .replace(/\{\{\s*max\s*\}\}/g, String(limit.effectiveLimit))
                .replace(/\{\{\s*cart\s*\}\}/g, String(currentQty));
        notifyBlocked(msg, limit.form);
        return buildBlockedResponse(msg);
      }
    }

    // Todo OK → continuar con el fetch original.
    return originalFetch(input, init);
  };
})();
